import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Invoice } from '../../../api/gql';
import { usePromiseWithTimeout } from '../../../hooks/usePromiseWithTimeout';
import { getConfig } from '../../../utils/config';

const { uploadEndpoint, uploadXmlEndpoint, uploadXlsEndpoint, uploadUnsupportedFileEndpoint } = getConfig();

export type SuccessResult = { id: string } | { scenarioId: string; claims: boolean; invoices: Partial<Invoice>[] };

export const useFileUpload = () => {
  const [loading, setLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const { getPromiseWithTimeout } = usePromiseWithTimeout();
  const { getAccessTokenSilently } = useAuth0();
  const uploadFile = async ({
    file,
    endpoint = uploadEndpoint,
    scenarioId,
    onSuccess,
    onError,
  }: {
    file: File;
    endpoint?: string;
    scenarioId?: string;
    onSuccess?: (res: SuccessResult) => void;
    onError?: (res: Response) => void;
  }) => {
    setLoading(true);

    const formData = new FormData();
    formData.append('file', file);

    const urlParams = new URLSearchParams({ scenarioId: scenarioId ?? '' });

    const endpointWithParams = `${endpoint}?${urlParams.toString()}`;

    const token = await getAccessTokenSilently();
    const res = await fetch(endpointWithParams, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!res.ok) {
      onError?.(res);
    } else {
      onSuccess?.(await res.json());
    }

    setLoading(false);
    return res;
  };

  const uploadUnsupportedFile = async ({
    file,
    onSuccess,
    onError,
    scenarioId,
  }: {
    file: File;
    endpoint?: string;
    scenarioId?: string;
    onSuccess?: (res: SuccessResult) => void;
    onError?: (res: Response) => void;
  }) =>
    uploadFile({
      file,
      onSuccess,
      onError,
      scenarioId,
      endpoint: uploadUnsupportedFileEndpoint,
    });

  const uploadBatch = async ({
    files,
    batchUploadId,
    onSuccess,
    onError,
    scenarioId,
  }: {
    files: File[];
    batchUploadId: string;
    onSuccess?: (res: SuccessResult) => void;
    onError?: (res: Response) => void;
    scenarioId?: string;
  }) => {
    const endpoint = `${uploadEndpoint}?batchUploadId=${batchUploadId}&scenarioId=${scenarioId ?? ''}`;

    files.forEach((file) => {
      uploadFile({
        file,
        endpoint,
        onSuccess,
        onError,
      });
    });
  };

  const uploadSpecifiedFormatFile = async ({
    file,
    minDuration = 0,
    onSuccess,
    onError,
    scenarioId,
    format = 'xml',
  }: {
    file: File;
    minDuration: number;
    onSuccess?: (res: SuccessResult) => void;
    onError?: (res: Response) => void;
    scenarioId?: string;
    format?: 'xml' | 'xls';
  }) => {
    setBatchLoading(true);

    const formData = new FormData();
    formData.append('file', file);

    const endpointUrl = format === 'xml' ? uploadXmlEndpoint : uploadXlsEndpoint;

    const urlParams = new URLSearchParams({ scenarioId: scenarioId ?? '' });

    const endpointWithParams = `${endpointUrl}?${urlParams.toString()}`;

    const token = await getAccessTokenSilently();
    const [res] = await getPromiseWithTimeout(
      fetch(endpointWithParams, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }),
      minDuration,
    );

    if (!res.ok) {
      onError?.(res);
    } else {
      onSuccess?.(await res.json());
    }

    setBatchLoading(false);

    return res;
  };

  const uploadXlsFile = async ({
    file,
    minDuration = 0,
    onSuccess,
    onError,
    scenarioId,
  }: {
    file: File;
    minDuration: number;
    onSuccess?: (res: SuccessResult) => void;
    onError?: (res: Response) => void;
    scenarioId?: string;
  }) => {
    setBatchLoading(true);

    const formData = new FormData();
    formData.append('file', file);

    const urlParams = new URLSearchParams({ scenarioId: scenarioId ?? '' });
    const endpointWithParams = `${uploadXmlEndpoint}?${urlParams.toString()}`;

    const token = await getAccessTokenSilently();
    const [res] = await getPromiseWithTimeout(
      fetch(endpointWithParams, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }),
      minDuration,
    );

    if (!res.ok) {
      onError?.(res);
    } else {
      onSuccess?.(await res.json());
    }

    setBatchLoading(false);

    return res;
  };

  return {
    uploadFile,
    uploadSpecifiedFormatFile,
    uploadBatch,
    uploadUnsupportedFile,
    state: {
      loading,
      batchLoading,
    },
  };
};
